import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiUsers,
  FiCheckCircle,
  FiGift,
  FiTrendingUp,
  FiArrowRight,
  FiArrowLeft,
} from "react-icons/fi";
import { QrCode } from "lucide-react";

const DashboardCard = ({ icon, title, value, color, onClick, isActive }) => (
  <button
    onClick={onClick}
    className={`w-full p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-md border-2 transition-all transform hover:scale-105 ${
      isActive
        ? "border-amber-500 bg-gradient-to-br from-amber-50 to-amber-100 shadow-lg"
        : "border-amber-200 bg-white hover:border-amber-400 hover:shadow-xl"
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="text-left flex-1">
        <p className="text-gray-600 text-xs sm:text-sm font-medium mb-1">{title}</p>
        <p className="text-2xl sm:text-3xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`text-3xl sm:text-4xl ${color} ml-3`}>{icon}</div>
    </div>
  </button>
);

const SortableTable = ({ columns, data, renderRow }) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const sortedData = useMemo(() => {
    const sorted = [...data];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [data, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <table className="border border-amber-500 rounded-lg min-w-full bg-white text-xs sm:text-sm text-left text-gray-950">
          <thead className="bg-amber-500 text-xs uppercase text-white">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="py-3 px-2 sm:px-4 border-b cursor-pointer select-none"
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable !== false && (
                      <span className="text-xs">
                        {sortConfig.key === col.key
                          ? sortConfig.direction === "asc"
                            ? "↑"
                            : "↓"
                          : "↕"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-300">
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="font-bold text-md text-center text-gray-950 py-6"
                >
                  {t("messages.noMatchFound")}
                </td>
              </tr>
            ) : (
              paginatedData.map(renderRow)
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 text-xs sm:text-sm bg-transparent">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-amber-500 text-white rounded-full disabled:opacity-50 shadow-sm"
          >
            {t("pagination.prev")}
          </button>
          <span className="text-gray-700">
            {t("pagination.pageOf")} {currentPage} {t("pagination.of")}{" "}
            {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-amber-500 text-white rounded-full disabled:opacity-50 shadow-sm"
          >
            {t("pagination.next")}
          </button>
        </div>
      )}
    </div>
  );
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();

  return (
    <div className="flex gap-2">
      <button
        onClick={() => i18n.changeLanguage("en")}
        className={`px-3 py-1 rounded-lg font-semibold transition-all text-sm ${
          i18n.language === "en"
            ? "bg-amber-500 text-white shadow-md"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
      >
        EN
      </button>
      <button
        onClick={() => i18n.changeLanguage("kn")}
        className={`px-3 py-1 rounded-lg font-semibold transition-all text-sm ${
          i18n.language === "kn"
            ? "bg-amber-500 text-white shadow-md"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
        }`}
      >
        ಕನ್ನಡ
      </button>
    </div>
  );
};

// Auto-scrolling Offers Carousel Component
const OffersCarousel = ({ offers }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!offers || offers.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % offers.length;
        if (carouselRef.current) {
          carouselRef.current.scrollTo({
            left: nextIndex * carouselRef.current.offsetWidth,
            behavior: 'smooth',
          });
        }
        return nextIndex;
      });
    }, 3000); // Auto-scroll every 3 seconds

    return () => clearInterval(interval);
  }, [offers]);

  if (!offers || offers.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 shadow-lg">
      <div
        ref={carouselRef}
        className="offers-carousel flex overflow-x-auto snap-x snap-mandatory scroll-smooth"
      >
        {offers.map((offer, index) => (
          <div
            key={offer.id || index}
            className="flex-shrink-0 w-full snap-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center p-4 sm:p-6 md:p-8 gap-4 sm:gap-6">
              {/* Offer Image */}
              <div className="flex-shrink-0 w-full sm:w-1/2 md:w-2/5">
                <img
                  src={offer.url_link || offer.image || 'https://via.placeholder.com/400x200?text=Offer'}
                  alt={offer.banner_type || offer.name || 'Offer'}
                  className="w-full h-48 sm:h-56 md:h-64 object-cover rounded-xl shadow-md"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Available';
                  }}
                />
              </div>
              {/* Offer Name/Details */}
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-4">
                  {offer.banner_type || offer.name || 'Special Offer'}
                </h3>
                {offer.client_name && (
                  <p className="text-sm sm:text-base text-gray-600 mb-2">
                    By: {offer.client_name}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Dots Indicator */}
      {offers.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
          {offers.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                if (carouselRef.current) {
                  carouselRef.current.scrollTo({
                    left: index * carouselRef.current.offsetWidth,
                    behavior: 'smooth',
                  });
                }
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-amber-600 w-6'
                  : 'bg-amber-300 hover:bg-amber-400'
              }`}
              aria-label={`Go to offer ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SalesmanDashboard = ({ data }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(null);

  const summary = data?.summary || {};
  const details = data?.details || {};
  const salesman = data?.salesman || {};
  const offers = data?.offers || data?.banners || []; // Get offers from API response

  const menuItems = [
    {
      name: t("salesmen.assignCustomers"),
      path: "/dashboard/salesmancustomerlist",
      icon: <FiUsers className="text-2xl sm:text-3xl" />,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      name: t("consumer.redemptionList"),
      path: "/dashboard/redemption-customers",
      icon: <FiCheckCircle className="text-2xl sm:text-3xl" />,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
    },
    {
      name: t("gifts.allocateGifts"),
      path: "/dashboard/giftList",
      icon: <FiGift className="text-2xl sm:text-3xl" />,
      color: "from-pink-500 to-pink-600",
      bgColor: "bg-pink-50",
    },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
  };

  if (selectedTab) {
    return (
      <div className="mt-2 max-w-full mx-auto rounded-lg p-2">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSelectedTab(null)}
              className="hover:text-gray-800"
            >
              <FiArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-amber-600">
              {tableConfig?.title}
            </h2>
          </div>
          <LanguageSwitcher />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8">
      {/* Hide scrollbar styles */}
      <style>{`
        .offers-carousel::-webkit-scrollbar {
          display: none;
        }
        .offers-carousel {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      
      {/* Language Switcher */}
      <div className="flex justify-end mb-4 sm:mb-6">
        <LanguageSwitcher />
      </div>

      {/* Offers Carousel - Auto-scrollable */}
      {offers && offers.length > 0 && (
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 px-2">
            {t("dashboard.specialOffers")}
          </h2>
          <OffersCarousel offers={offers} />
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <DashboardCard
          icon={<FiUsers />}
          title={t("dashboard.customers")}
          value={summary.total_customers_created || 0}
          color="text-blue-600"
        />
        <DashboardCard
          icon={<QrCode className="w-8 h-8" />}
          title={t("dashboard.qrAccumulated")}
          value={summary.total_qr_codes_accumulated || 0}
          color="text-purple-600"
        />
        <DashboardCard
          icon={<FiCheckCircle />}
          title={t("dashboard.qrRedeemed")}
          value={summary.total_qr_codes_redeemed || 0}
          color="text-green-600"
        />
        <DashboardCard
          icon={<FiGift />}
          title={t("dashboard.giftsRedeemed")}
          value={summary.total_gifts_redeemed || 0}
          color="text-pink-600"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border-2 border-amber-200 overflow-hidden">
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {menuItems.map((item, index) => (
              <button
                key={item.path}
                onClick={() => handleMenuClick(item.path)}
                className={`group relative overflow-hidden rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-amber-200 bg-gradient-to-br ${item.bgColor} hover:border-amber-400 hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className={`bg-gradient-to-br ${item.color} p-4 rounded-full shadow-lg group-hover:shadow-xl transition-all`}>
                    <div className="text-white">
                      {item.icon}
                    </div>
                  </div>
                  <span className="text-sm sm:text-base font-semibold text-gray-800 group-hover:text-amber-700 transition-colors">
                    {item.name}
                  </span>
                  <FiArrowRight className="text-amber-600 group-hover:text-amber-700 group-hover:translate-x-1 transition-all" />
                </div>
                
                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl sm:rounded-2xl"></div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesmanDashboard;